import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import "../../../styles/payrole-system/payrole-forms.css";
import {
  currentMonth,
  currentYearNo,
  months,
  years,
} from "../../common-data/DropdowData";
import CloseSubmitBtn from "../../common-components/custom-comps/CloseSubmitBtn";
import { showConfirmAlert } from "../../components-react/ReactAlert";
import { commonPostPutApi } from "../../../redux/reducers/commonApiSlice";

const PayroleProcessForm = ({
  userInfoData,
  selectedIds,
  onClose,
  callFunction,
  isRegenerate,
  rowData,
}) => {
  const dispatch = useDispatch();
  const { postPutLoading } = useSelector((state) => state.common);

  const prevMonthValue = (Number(currentMonth) - 1).toString();
  const prevMonthObj = months.find((m) => m.value === prevMonthValue);

  const getInitialFormValues = () => {
    if (isRegenerate && rowData?.date) {
      const dateObj = new Date(rowData.date);
      const year = dateObj.getFullYear().toString();
      const month = (dateObj.getMonth() + 1).toString();

      const selectedYearObj = years.find((y) => y.value === year);
      const selectedMonthObj = months.find((m) => m.value === month);

      return {
        selectedYear:
          selectedYearObj ||
          years.find((y) => y.value === currentYearNo.toString()),
        selectedMonth:
          selectedMonthObj || prevMonthObj || months[months.length - 1],
      };
    } else {
      return {
        selectedYear: years.find((y) => y.value === currentYearNo.toString()),
        selectedMonth: prevMonthObj || months[months.length - 1],
      };
    }
  };

  const [formValues, setFormValues] = useState(getInitialFormValues());
  const [formErrors, setFormErrors] = useState({});
  const [showSuccess, setShowSuccess] = useState(false);

  const filteredMonths =
    formValues.selectedYear?.value === currentYearNo.toString()
      ? months.filter((m) => Number(m.value) < Number(currentMonth))
      : months;

  const validateForm = (values) => {
    const errors = {};

    if (!values.selectedYear) {
      errors.selectedYear = "Year is required!";
    }

    if (!values.selectedMonth) {
      errors.selectedMonth = "Month is required!";
    }

    return errors;
  };

  const handleSubmitForm = async (payload) => {
    const method = "POST";
    const endpoint = "/payrole/generate";
    const token = userInfoData?.token || "";

    try {
      const result = await dispatch(
        commonPostPutApi({ method, endpoint, payload, token })
      );

      if (result?.payload?.status) {
        setFormValues(getInitialFormValues());
        setShowSuccess(true);
        callFunction();
        toast.success(result?.payload?.message || "Success!");
      } else {
        const errorMessage = result?.payload?.message || result?.error?.message;
        toast.error(errorMessage || "Something Went Wrong!");
      }
    } catch (error) {
      console.error("Error making POST request:", error);
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    const errors = validateForm(formValues);
    setFormErrors(errors);

    if (Object.keys(errors).length === 0) {
      const userData = {
        user_id:
          isRegenerate && rowData?.user_id && !selectedIds?.length
            ? [rowData?.user_id]
            : selectedIds?.map((id) => id?.user_id) || [],
        month:
          formValues.selectedYear?.value && formValues.selectedMonth?.value
            ? `${
                formValues.selectedYear.value
              }-${formValues.selectedMonth.value.padStart(2, "0")}-01`
            : "",
        regenerate: isRegenerate ? true : false,
      };

      const handleConfirm = async () => {
        handleSubmitForm(userData);
      };

      showConfirmAlert(
        handleConfirm,
        `Are you sure you want to process payroll for ${
          formValues.selectedMonth?.label
        } ${formValues.selectedYear?.label} for ${
          rowData?.user_name || selectedIds.length
        } staff?`,
        "No, Cancel",
        "Yes, Submit"
      );
    }
  };

  useEffect(() => {
    if (
      !isRegenerate &&
      formValues.selectedMonth &&
      !filteredMonths.some(
        (m) => m.value.toString() === formValues.selectedMonth.value.toString()
      )
    ) {
      setFormValues((prev) => ({
        ...prev,
        selectedMonth: filteredMonths[filteredMonths.length - 1] || null,
      }));
    }
  }, [filteredMonths, isRegenerate]);

  if (showSuccess) {
    return (
      <div className="pp-payroll-success-ui">
        <div className="pp-payroll-success-icon">✅</div>
        <div className="pp-payroll-success-title">
          Payroll Processed Successfully!
        </div>
        <div className="pp-payroll-success-desc">
          Payroll for {formValues.selectedMonth?.label}{" "}
          {formValues.selectedYear?.label} has been processed for{" "}
          {rowData?.user_name || selectedIds.length} staff.
        </div>
        <button className="pp-btn-process btn-dark" onClick={onClose}>
          Close
        </button>
      </div>
    );
  }

  return (
    <form className="formContainer pp-payroll-form-container pp-animate-in">
      <div className="pp-payroll-form-header">
        <span>
          total staff selected: {rowData?.user_name || selectedIds.length}
        </span>
      </div>

      <div className="formInputsGrid flex-system">
        <div className="formInputBox">
          <label htmlFor="selectedYear" className="formInputLabel">
            choose year for payroll month <span className="required">*</span>
          </label>
          <div className="pp-payroll-group">
            {years.map((year) => (
              <label
                key={year.value}
                className={`pp-payroll-pill 
                  ${isRegenerate ? "disabled" : ""}  
                 ${
                   formValues.selectedYear?.value === year.value
                     ? "selected"
                     : ""
                 }`}
              >
                <input
                  type="radio"
                  name="selectedYear"
                  checked={formValues.selectedYear?.value === year.value}
                  onChange={() =>
                    setFormValues((prev) => ({
                      ...prev,
                      selectedYear:
                        prev.selectedYear?.value === year.value ? "" : year,
                      selectedMonth: "",
                    }))
                  }
                  className="pp-payroll-radio"
                  disabled={isRegenerate}
                />
                {year.label}
              </label>
            ))}
          </div>
          <span className="error-label">{formErrors?.selectedYear || ""}</span>
        </div>

        <div className="formInputBox">
          <label htmlFor="selectedMonth" className="formInputLabel">
            choose Month to process payroll <span className="required">*</span>
          </label>

          <div className="pp-payroll-group">
            {filteredMonths.map((month) => (
              <label
                key={month.value}
                className={`pp-payroll-pill${
                  formValues.selectedMonth?.value?.toString() ===
                  month.value.toString()
                    ? " selected"
                    : ""
                }${isRegenerate ? " disabled" : ""}`}
              >
                <input
                  type="radio"
                  name="selectedMonth"
                  checked={
                    formValues.selectedMonth?.value?.toString() ===
                    month.value.toString()
                  }
                  onChange={() =>
                    setFormValues((prev) => ({
                      ...prev,
                      selectedMonth:
                        prev.selectedMonth?.value?.toString() ===
                        month.value.toString()
                          ? null
                          : month,
                    }))
                  }
                  className="pp-payroll-radio"
                  disabled={isRegenerate}
                />
                {month.label}
              </label>
            ))}
          </div>
          <span className="error-label">{formErrors?.selectedMonth || ""}</span>
        </div>
      </div>

      <CloseSubmitBtn
        closeLabel="Cancel"
        submitLabel={"submit"}
        onClose={onClose}
        onSubmit={onSubmit}
        isLoading={postPutLoading}
      />
    </form>
  );
};

export default PayroleProcessForm;
